import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  ArrowLeft,
  Play,
  Pause,
  Download,
  Clock,
  Phone,
  User,
  Calendar,
  Volume2,
  VolumeX,
  Search,
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertTriangle,
  Smile,
  Frown,
  Meh,
  ClipboardCheck,
  History,
  EyeOff,
  Eye,
} from "lucide-react";

const CallDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authAxios, token } = useAuth();
  const audioRef = useRef(null);

  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSegment, setActiveSegment] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioError, setAudioError] = useState(false);
  const [showSilences, setShowSilences] = useState(false);
  const [repeatedCalls, setRepeatedCalls] = useState([]);
  const transcriptRef = useRef(null);

  const fetchCall = async () => {
    try {
      const response = await authAxios.get(`/calls/${id}`);
      setCall(response.data);
      
      // Set audio URL for streaming
      if (response.data.guid) {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        setAudioUrl(`${backendUrl}/api/ucontact/audio-stream/${response.data.guid}`);
      }
    } catch (error) {
      console.error("Error fetching call:", error);
      toast.error("Error al cargar la llamada");
      navigate("/calls");
    } finally {
      setLoading(false);
    }
  };

  const fetchRepeatedCalls = async (phoneNumber, currentCallId) => {
    if (!phoneNumber) return;
    try {
      const response = await authAxios.get(`/calls?phone_number=${encodeURIComponent(phoneNumber)}`);
      // Filter out the current call
      const history = response.data.items.filter(c => c.id !== currentCallId);
      setRepeatedCalls(history);
    } catch (error) {
      console.error("Error fetching history:", error);
    }
  };

  useEffect(() => {
    fetchCall();
  }, [id]);

  useEffect(() => {
    if (call?.phone_number) {
      fetchRepeatedCalls(call.phone_number, call.id);
    }
  }, [call?.phone_number]);

  // Setup audio element when URL is available
  useEffect(() => {
    if (audioRef.current && audioUrl) {
      audioRef.current.src = audioUrl;
      setAudioError(false);
    }
  }, [audioUrl]);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      await authAxios.post(`/calls/${id}/analyze`);
      toast.success("Análisis iniciado");
      setTimeout(fetchCall, 3000);
    } catch (error) {
      toast.error("Error al iniciar análisis");
    } finally {
      setAnalyzing(false);
    }
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      
      // Find active segment
      if (call?.transcription) {
        const time = audioRef.current.currentTime;
        const segment = call.transcription.find((s, i) => {
          const segTime = parseTimeToSeconds(s.time);
          const nextTime = call.transcription[i + 1] 
            ? parseTimeToSeconds(call.transcription[i + 1].time)
            : duration;
          return time >= segTime && time < nextTime;
        });
        if (segment) {
          setActiveSegment(segment.time);
          // Auto-scroll logic
          if (transcriptRef.current) {
            const container = transcriptRef.current;
            const activeEl = container.querySelector('.transcript-segment.active');
            if (activeEl) {
              const scrollTarget = activeEl.offsetTop - (container.clientHeight / 2) + (activeEl.clientHeight / 2);
              if (Math.abs(container.scrollTop - scrollTarget) > 30) {
                container.scrollTo({ top: scrollTarget, behavior: 'smooth' });
              }
            }
          }
        }
      }
    }
  };

  const parseTimeToSeconds = (timeStr) => {
    if (!timeStr) return 0;
    const parts = timeStr.split(":");
    if (parts.length === 2) {
      return parseInt(parts[0]) * 60 + parseInt(parts[1]);
    }
    return 0;
  };

  const formatTime = (seconds) => {
    if (!seconds || !isFinite(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const seekTo = (timeStr) => {
    if (audioRef.current) {
      const seconds = parseTimeToSeconds(timeStr);
      audioRef.current.currentTime = seconds;
      setActiveSegment(timeStr);
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const getSentimentIcon = (sentiment) => {
    if (!sentiment) return <Meh className="w-5 h-5 text-slate-400" />;
    if (sentiment >= 7) return <Smile className="w-5 h-5 text-green-500" />;
    if (sentiment >= 4) return <Meh className="w-5 h-5 text-yellow-500" />;
    return <Frown className="w-5 h-5 text-red-500" />;
  };

  const getSentimentBadgeClass = (sentiment) => {
    if (!sentiment) return "bg-slate-100 text-slate-600";
    if (sentiment >= 7) return "bg-green-100 text-green-700";
    if (sentiment >= 4) return "bg-yellow-100 text-yellow-700";
    return "bg-red-100 text-red-700";
  };

  const filteredTranscription = call?.transcription?.filter(segment =>
    searchTerm === "" || segment.text.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  if (!call) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Llamada no encontrada</p>
        <Button onClick={() => navigate("/calls")} className="mt-4">
          Volver a llamadas
        </Button>
      </div>
    );
  }

  const sentiment = call.sentiment_analysis;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/calls")}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900">
                Llamada #{call.ucontact_id || call.id.slice(0, 8)}
              </h1>
              {call.is_analyzed ? (
                <Badge className="bg-green-100 text-green-700">Analizada</Badge>
              ) : (
                <Badge className="bg-slate-100 text-slate-600">Pendiente</Badge>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {call.call_date ? format(new Date(call.call_date), "dd/MM/yyyy HH:mm", { locale: es }) : "-"}
              </span>
              <span className="flex items-center gap-1">
                <User className="w-4 h-4" />
                {call.agent || "Sin agente"}
              </span>
              <span className="flex items-center gap-1">
                <Phone className="w-4 h-4" />
                {call.source} → {call.destination}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {!call.is_analyzed && (
            <Button
              onClick={handleAnalyze}
              disabled={analyzing}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="analyze-call-btn"
            >
              {analyzing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Analizar Llamada
            </Button>
          )}
          <Button variant="outline" data-testid="download-btn">
            <Download className="w-4 h-4 mr-2" />
            Descargar
          </Button>
        </div>
      </div>

      {/* Sentiment Cards */}
      {call.is_analyzed && sentiment && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <Card className="bg-white" data-testid="sentiment-general">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500 mb-1">Sentimiento General</p>
              <p className="text-3xl font-bold text-sky-600">{sentiment.general_sentiment?.toFixed(1) || "N/A"}</p>
            </CardContent>
          </Card>
          <Card className="bg-white" data-testid="sentiment-opening">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500 mb-1">Apertura</p>
              <p className="text-3xl font-bold text-slate-700">{sentiment.opening_sentiment?.toFixed(1) || "N/A"}</p>
            </CardContent>
          </Card>
          <Card className="bg-white" data-testid="sentiment-closing">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500 mb-1">Cierre</p>
              <p className="text-3xl font-bold text-slate-700">{sentiment.closing_sentiment?.toFixed(1) || "N/A"}</p>
            </CardContent>
          </Card>
          <Card className="bg-white" data-testid="call-duration">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500 mb-1">Duración</p>
              <p className="text-3xl font-bold text-slate-700">{formatTime(call.duration)}</p>
            </CardContent>
          </Card>
          <Card className="bg-white" data-testid="risk-score">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500 mb-1">Riesgo</p>
              <p className={`text-3xl font-bold ${call.risk_score >= 50 ? 'text-red-600' : 'text-green-600'}`}>
                {call.risk_score?.toFixed(0) || 0}%
              </p>
            </CardContent>
          </Card>
          <Card className="bg-white" data-testid="detected-topics">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500 mb-1">Temas</p>
              <p className="text-3xl font-bold text-slate-700">{call.detected_topics?.length || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Audio & Transcription */}
        <div className="lg:col-span-2 space-y-6">
          {/* Audio Player */}
          <Card data-testid="audio-player-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold">Reproductor de Audio</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Waveform visualization */}
              <div className="audio-waveform mb-4">
                {Array.from({ length: 80 }).map((_, i) => {
                  const height = Math.random() * 100;
                  const isPlayed = (i / 80) <= (currentTime / (duration || 1));
                  return (
                    <div
                      key={i}
                      className={`audio-waveform-bar ${isPlayed ? 'played' : ''}`}
                      style={{ height: `${height}%` }}
                    />
                  );
                })}
              </div>

              {/* Controls */}
              <div className="flex items-center gap-4">
                <Button
                  size="icon"
                  className={`h-12 w-12 rounded-full ${audioError ? 'bg-slate-400' : 'bg-sky-600 hover:bg-sky-700'}`}
                  onClick={togglePlay}
                  disabled={audioError}
                  data-testid="play-pause-btn"
                >
                  {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                </Button>

                <div className="flex-1">
                  <input
                    type="range"
                    min={0}
                    max={(duration && isFinite(duration) ? duration : call?.duration) || 100}
                    value={currentTime}
                    onChange={(e) => {
                      if (audioRef.current) {
                        audioRef.current.currentTime = parseFloat(e.target.value);
                      }
                    }}
                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-sky-600"
                    data-testid="audio-seek-slider"
                    disabled={audioError}
                  />
                  <div className="flex justify-between text-sm text-slate-500 mt-1">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime((duration && isFinite(duration) ? duration : call?.duration))}</span>
                  </div>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleMute}
                  disabled={audioError}
                  data-testid="mute-btn"
                >
                  {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </Button>
              </div>

              {audioError && (
                <p className="text-center text-sm text-slate-500 mt-2">
                  Audio no disponible desde uContact. {call?.is_simulated ? "La transcripción está basada en análisis simulado." : "Mostrando la transcripción registrada."}
                </p>
              )}

              {/* Hidden audio element */}
              <audio
                ref={audioRef}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={() => {
                  const dur = audioRef.current?.duration;
                  if (dur && isFinite(dur)) {
                    setDuration(dur);
                  } else {
                    setDuration(call?.duration || 0);
                  }
                }}
                onEnded={() => setIsPlaying(false)}
                onError={() => setAudioError(true)}
              />
            </CardContent>
          </Card>

          {/* Transcription */}
          <Card data-testid="transcription-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold">Transcripción de la Llamada</CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSilences(!showSilences)}
                    className="text-xs"
                  >
                    {showSilences ? <EyeOff className="w-3 h-3 mr-1" /> : <Eye className="w-3 h-3 mr-1" />}
                    Silencios
                  </Button>
                  <div className="relative w-48">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Buscar..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 h-8 text-sm"
                      data-testid="transcription-search"
                    />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div ref={transcriptRef} className="space-y-3 max-h-[500px] overflow-y-auto pr-2 relative">
                {call.transcription && call.transcription.length > 0 ? (
                  filteredTranscription.map((segment, i) => {
                    // Calculate silence before this segment
                    let silenceElement = null;
                    if (showSilences && i > 0) {
                      const prevSegment = filteredTranscription[i - 1];
                      const prevEnd = parseTimeToSeconds(prevSegment.time) + (prevSegment.text.length * 0.05); // Rough estimation of end time
                      const currStart = parseTimeToSeconds(segment.time);
                      const gap = currStart - prevEnd;
                      
                      if (gap >= 3) {
                        silenceElement = (
                          <div className="flex items-center justify-center my-2 text-xs text-slate-400 bg-slate-50 py-1 rounded-full border border-slate-100 w-fit mx-auto px-4">
                            Silencio prolongado ({Math.round(gap)}s)
                          </div>
                        );
                      }
                    }

                    return (
                      <div key={i}>
                        {silenceElement}
                        <div
                          className={`transcript-segment ${segment.speaker === 'agent' ? 'agent' : 'customer'} ${activeSegment === segment.time ? 'active' : ''} cursor-pointer hover:bg-slate-50 p-2 rounded-lg transition-colors border ${activeSegment === segment.time ? 'border-sky-300 bg-sky-50' : 'border-transparent'}`}
                          onClick={() => seekTo(segment.time)}
                          data-testid={`transcript-segment-${i}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className="text-xs bg-white">
                                  {segment.time}
                                </Badge>
                                <span className="text-xs font-semibold text-slate-600 capitalize">
                                  {segment.speaker === 'agent' ? 'Agente' : 'Cliente'}
                                </span>
                              </div>
                              <p className="text-slate-800 leading-relaxed text-sm">{segment.text}</p>
                            </div>
                            {segment.sentiment && (
                              <Badge className={getSentimentBadgeClass(segment.sentiment)}>
                                {segment.sentiment?.toFixed(1)}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <p>No hay transcripción disponible</p>
                    {!call.is_analyzed && (
                      <Button onClick={handleAnalyze} className="mt-4" disabled={analyzing}>
                        Analizar para obtener transcripción
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Analysis Results */}
        <div className="space-y-6">
          {/* Summary */}
          {sentiment?.summary && (
            <Card data-testid="call-summary">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold">Resumen</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">{sentiment.summary}</p>
              </CardContent>
            </Card>
          )}

          {/* Topics */}
          {call.detected_topics && call.detected_topics.length > 0 && (
            <Card data-testid="call-topics">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold">Temas Detectados</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {call.detected_topics.map((topic, i) => (
                    <Badge key={i} className="bg-sky-100 text-sky-700">
                      {topic}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Keywords */}
          {sentiment?.keywords && sentiment.keywords.length > 0 && (
            <Card data-testid="call-keywords">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold">Palabras Clave</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {sentiment.keywords.map((kw, i) => (
                    <Badge key={i} variant="outline" className="font-normal">
                      {kw.word} ({kw.count})
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Risk Factors */}
          {sentiment?.risk_factors && sentiment.risk_factors.length > 0 && (
            <Card className="border-orange-200 bg-orange-50" data-testid="risk-factors">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold flex items-center gap-2 text-orange-700">
                  <AlertTriangle className="w-5 h-5" />
                  Factores de Riesgo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {sentiment.risk_factors.map((factor, i) => (
                    <li key={i} className="flex items-start gap-2 text-orange-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 flex-shrink-0" />
                      {factor}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Matrix Evaluation */}
          {call.evaluation_result && (
            <Card className="border-sky-200 bg-sky-50" data-testid="matrix-evaluation">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold flex items-center gap-2 text-sky-700">
                  <ClipboardCheck className="w-5 h-5" />
                  Evaluación: {call.evaluation_result.matrix_name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Total Score */}
                  <div className="text-center p-4 bg-white rounded-lg">
                    <p className="text-sm text-slate-500">Puntuación Total</p>
                    <p className={`text-4xl font-bold ${
                      call.evaluation_result.total_score >= 7 ? 'text-green-600' :
                      call.evaluation_result.total_score >= 5 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {call.evaluation_result.total_score?.toFixed(1) || 'N/A'}
                    </p>
                  </div>

                  {/* Criteria Scores */}
                  {call.evaluation_result.criteria_scores?.map((criterion, i) => (
                    <div key={i} className="bg-white rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-slate-700">{criterion.name}</span>
                        <Badge className={
                          criterion.score >= 7 ? 'bg-green-100 text-green-700' :
                          criterion.score >= 5 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                        }>
                          {criterion.score}/10
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500">{criterion.justification}</p>
                    </div>
                  ))}

                  {/* Overall Feedback */}
                  {call.evaluation_result.overall_feedback && (
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-sm font-medium text-slate-700 mb-1">Retroalimentación General</p>
                      <p className="text-sm text-slate-600">{call.evaluation_result.overall_feedback}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Metadata */}
          <Card data-testid="call-metadata">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold">Metadatos</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <dt className="text-slate-500">GUID</dt>
                  <dd className="font-mono text-slate-700">{call.guid?.slice(0, 12)}...</dd>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <dt className="text-slate-500">Campaña</dt>
                  <dd className="text-slate-700">{call.campaign || "-"}</dd>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <dt className="text-slate-500">Dirección</dt>
                  <dd className="text-slate-700 capitalize">{call.direction || "-"}</dd>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <dt className="text-slate-500">Duración</dt>
                  <dd className="text-slate-700">{formatTime(call.duration)}</dd>
                </div>
                {call.analyzed_at && (
                  <>
                    <Separator />
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Analizada</dt>
                      <dd className="text-slate-700">
                        {format(new Date(call.analyzed_at), "dd/MM/yyyy HH:mm", { locale: es })}
                      </dd>
                    </div>
                  </>
                )}
              </dl>
            </CardContent>
          </Card>

          {/* Historial del Cliente (Repeated Interactions) */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <History className="w-5 h-5 text-indigo-600" />
                Historial del Cliente
              </CardTitle>
            </CardHeader>
            <CardContent>
              {repeatedCalls.length > 0 ? (
                <div className="space-y-3">
                  <p className="text-sm text-slate-500 mb-2">
                    Este cliente ({call.phone_number}) ha llamado {repeatedCalls.length} vez/veces antes:
                  </p>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto pr-1">
                    {repeatedCalls.map(rc => (
                      <div 
                        key={rc.id} 
                        className="p-2 border rounded-md hover:bg-slate-50 cursor-pointer flex justify-between items-center"
                        onClick={() => {
                          navigate(`/calls/${rc.id}`);
                          window.scrollTo(0, 0);
                        }}
                      >
                        <div>
                          <p className="text-xs font-semibold text-slate-700">{rc.call_date ? format(new Date(rc.call_date), "dd/MM/yyyy HH:mm") : "-"}</p>
                          <p className="text-xs text-slate-500">Agente: {rc.agent}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center p-4">
                  <p className="text-sm text-slate-500">No hay registros previos de este número.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CallDetailPage;
